import { create } from 'zustand'

export type LayoutMode = 'auto' | 'tiled' | 'spotlight' | 'sidebar' | 'pinned' | 'dual-stream'
/**
 * How many TILES the grid shows at once — your own included.
 *
 * Any positive number, not a fixed set, because Settings lets the user name one
 * directly. Note the unit: this counts tiles, while the control that writes it
 * counts other people. `tilesForVisibleVideos` below is the whole difference,
 * and it is the reason this is not simply the dropdown's value.
 */
export type TileDensity = 'auto' | number

/**
 * What "Auto" means in Settings -> "Select number of visible videos on Screen".
 *
 * Counted the way that control is worded: OTHER people. Your own tile sits in
 * the grid too, so Auto renders 16 tiles — you and fifteen others, which is the
 * first page the meeting has always been built around.
 */
export const AUTO_VISIBLE_VIDEOS = 15

/** Tiles on a page when the density is 'auto' — the fifteen, plus you. */
export const AUTO_PAGE_SIZE = AUTO_VISIBLE_VIDEOS + 1

/**
 * Tiles <-> "visible videos", the two units these controls speak in.
 *
 * Settings counts the people you are looking AT; the grid counts the tiles it
 * has to draw, and one of those is you. Keeping both conversions here, next to
 * the value they convert, is what stops the two dropdowns that write this field
 * from disagreeing by one about what is on screen.
 */
export const tilesForVisibleVideos = (videos: number): number => Math.max(1, videos) + 1
export const visibleVideosForTiles = (tiles: number): number => Math.max(1, tiles - 1)

type MeetingLayoutState = {
  layoutMode: LayoutMode
  gridPage: number
  rightRailPage: number
  pinnedPage: number
  pinnedIds: string[]
  tileDensity: TileDensity
  hideTilesWithoutVideo: boolean
  /**
   * Moderator only: push this client's layout to everyone in the room.
   *
   * Deliberately NOT persisted. It is an action taken during one meeting, and a
   * moderator who left it on would start moving the next room's screens the
   * moment they joined, before deciding to.
   */
  layoutSyncEnabled: boolean

  setLayoutMode: (mode: LayoutMode) => void
  setGridPage: (page: number | ((prev: number) => number)) => void
  setRightRailPage: (page: number | ((prev: number) => number)) => void
  setPinnedPage: (page: number | ((prev: number) => number)) => void
  setPinnedIds: (ids: string[] | ((prev: string[]) => string[])) => void
  setTileDensity: (d: TileDensity) => void
  setHideTilesWithoutVideo: (v: boolean) => void
  setLayoutSyncEnabled: (enabled: boolean) => void
  handlePin: (participantId: string) => void
  /** "Pin for all" arriving: that one tile pinned, as a single pin shows. */
  pinOnly: (participantId: string) => void
  /** "Unpin for all" arriving: back to the ordinary grid. */
  clearPins: () => void
}

const PIN_LIMIT = 6

export const useMeetingLayoutStore = create<MeetingLayoutState>((set, get) => ({
  layoutMode: 'auto',
  gridPage: 0,
  rightRailPage: 0,
  pinnedPage: 0,
  pinnedIds: ['you'],
  tileDensity: 'auto',
  hideTilesWithoutVideo: false,
  layoutSyncEnabled: false,

  setLayoutMode: (layoutMode) => set({ layoutMode }),

  setLayoutSyncEnabled: (layoutSyncEnabled) => set({ layoutSyncEnabled }),
  setGridPage: (page) =>
    set({ gridPage: typeof page === 'function' ? page(get().gridPage) : page }),
  setRightRailPage: (page) =>
    set({ rightRailPage: typeof page === 'function' ? page(get().rightRailPage) : page }),
  setPinnedPage: (page) =>
    set({ pinnedPage: typeof page === 'function' ? page(get().pinnedPage) : page }),
  setPinnedIds: (ids) =>
    set({ pinnedIds: typeof ids === 'function' ? ids(get().pinnedIds) : ids }),
  setTileDensity: (tileDensity) => set({ tileDensity }),
  setHideTilesWithoutVideo: (hideTilesWithoutVideo) => set({ hideTilesWithoutVideo }),

  // Exactly the state "Pin for me" gives from a clean screen, so a tile pinned
  // for everyone behaves like one you pinned yourself: same layout, and the
  // tile's menu reads "Unpin". Writing `[id]` directly skipped the `'you'`
  // resting entry, and the stage then did not count its own tile as pinned.
  pinOnly: (participantId) => {
    set({ pinnedIds: ['you'], layoutMode: 'auto', pinnedPage: 0, rightRailPage: 0 })
    get().handlePin(participantId)
  },

  clearPins: () => set({ pinnedIds: ['you'], layoutMode: 'auto', pinnedPage: 0, rightRailPage: 0 }),

  handlePin: (participantId) => {
    const current = get().pinnedIds
    const isAlreadyPinned = current.includes(participantId)

    if (isAlreadyPinned) {
      const filtered = current.filter((id) => id !== participantId)

      // Unpinning the last participant goes back to the ordinary grid.
      //
      // `['you']` is the resting state rather than a pin — the store starts
      // there, with layout `auto`. Unpinning used to leave exactly that and
      // then choose `sidebar`, because one id looks like one pin. So removing
      // the only pin put you on the stage instead, with the side rail and its
      // drag handle still on screen: nothing had been unpinned as far as the
      // screen was concerned.
      const nothingPinned = filtered.length === 0 || (filtered.length === 1 && filtered[0] === 'you')
      if (nothingPinned) {
        set({ pinnedIds: ['you'], layoutMode: 'auto', pinnedPage: 0, rightRailPage: 0 })
        return
      }

      set({
        pinnedIds: filtered,
        layoutMode: filtered.length === 1 ? 'sidebar' : 'pinned',
      })
      return
    }

    if (current.length >= PIN_LIMIT) {
      set({
        pinnedIds: [...current.slice(0, PIN_LIMIT - 1), participantId],
        layoutMode: 'pinned',
      })
      return
    }

    const newPinned = [...current, participantId]
    set({
      pinnedIds: newPinned,
      layoutMode: newPinned.length === 1 ? 'sidebar' : 'pinned',
    })
  },
}))

/**
 * How many tiles one page of the grid holds.
 *
 * The single answer for both grids. `tiled` slices the roster with it in
 * MeetingScreen; `auto` hands it to VideoGrid as `pageSize`. Two numbers here
 * would mean the page you can see and the page the active-speaker rule reasons
 * about could differ, which is exactly how a speaker gets promoted onto a page
 * they were already on.
 *
 * It also decides which videos are consumed: tiles declare themselves to
 * rendered-tiles, and lastN pauses every camera that is not among them. So this
 * number is bandwidth as much as it is layout.
 */
export const selectMaxTiles = (s: MeetingLayoutState): number =>
  s.tileDensity !== 'auto' ? s.tileDensity : AUTO_PAGE_SIZE

/**
 * Most tiles a phone or tablet draws on one page, whatever the density says.
 * Past six the tiles on a phone are too small to recognise anyone in.
 */
export const TOUCH_DEVICE_MAX_TILES = 6

/**
 * Tiles on the page actually drawn: the density, capped on a phone or tablet.
 *
 * VideoGrid pages with this, and the active-speaker seating has to reason with
 * the same number. Seating with `maxTiles` on a phone counted seats 6-15 as
 * "already visible" when they were on page two, so a speaker sitting there was
 * never brought onto the screen. Desktop is untouched: the cap only applies
 * when `touchLimited`.
 */
export const visibleTilesPerPage = (maxTiles: number, touchLimited: boolean): number =>
  touchLimited ? Math.min(maxTiles, TOUCH_DEVICE_MAX_TILES) : maxTiles
